export const REVIEW_BONUS = 0.18;
export const ACTIVATION_THRESHOLD = 0.55;

export const constructSpecs = [
  {
    id: 'cog.representation.global_local_order',
    label: 'Global-local representation order',
    definition: 'Which representation is useful first: a whole-system frame or component sequence.',
    lowAnchor: 'components first',
    highAnchor: 'system frame first',
    alternatives: ['task familiarity', 'domain expertise', 'presentation wording'],
    mappings: {
      'uci.q01': { A: -1.0, B: 1.0, C: -0.4, D: 0.6 },
      'uci.q04': { A: -1.0, B: 1.0, C: 0.6, D: -0.2 },
      'uci.q19': { A: -1.0, B: 0.1, C: 0.2, D: 1.0 }
    }
  },
  {
    id: 'cog.processing.sequential_relational',
    label: 'Sequential-relational processing',
    category: 'cognition',
    definition: 'Whether an initial explanation is most useful as an ordered sequence or a relationship network.',
    lowAnchor: 'ordered sequence',
    highAnchor: 'relationship network',
    alternatives: ['problem type', 'domain experience', 'visual presentation'],
    mappings: {
      'uci.q01': { A: -1.0, B: 1.0, C: -0.7, D: 0.4 },
      'uci.q03': { A: -1.0, B: -0.4, C: 1.0, D: 0.5 },
      'uci.q08': { A: -0.9, B: -0.4, C: 1.0, D: -0.6 }
    }
  },
  {
    id: 'cog.filter.closure_threshold',
    label: 'Information closure threshold',
    category: 'cognition',
    definition: 'How much task-relevant evidence is useful before forming a working conclusion.',
    lowAnchor: 'early working closure',
    highAnchor: 'extended evidence search',
    alternatives: ['stakes', 'time pressure', 'topic familiarity'],
    mappings: {
      'uci.q02': { A: -1.0, B: 0.1, C: 0.6, D: 1.0 },
      'uci.q11': { A: -0.5, B: 0.7, C: -1.0, D: 1.0 },
      'uci.q12': { A: -1.0, B: -0.5, C: 0.3, D: 1.0 }
    }
  },
  {
    id: 'cog.reasoning.probabilistic',
    label: 'Uncertainty framing',
    category: 'cognition',
    definition: 'How uncertainty is most usefully represented when evidence is incomplete.',
    lowAnchor: 'single working answer',
    highAnchor: 'ranges, scenarios, and update conditions',
    alternatives: ['numeracy', 'risk', 'decision urgency'],
    mappings: {
      'uci.q05': { A: -1.0, B: 0.4, C: 1.0, D: 0.8 },
      'uci.q17': { A: 0.8, B: 0.5, C: 0.9, D: 1.0 }
    }
  },
  {
    id: 'cog.search.divergence_convergence',
    label: 'Search breadth before commitment',
    category: 'cognition',
    definition: 'How broadly to generate alternatives before selecting and refining a direction.',
    lowAnchor: 'converge quickly',
    highAnchor: 'diverge broadly',
    alternatives: ['deadline', 'solution familiarity', 'cost of exploration'],
    mappings: {
      'uci.q07': { A: -1.0, B: 0.2, C: -0.2, D: 1.0 }
    }
  },
  {
    id: 'motivation.improvement_target',
    label: 'Improvement target orientation',
    category: 'personality_motivation',
    definition: 'Whether improvement attention begins with personal execution or wider people, process, and possibility effects.',
    lowAnchor: 'personal execution',
    highAnchor: 'system or possibility',
    alternatives: ['current role', 'project stage', 'available authority'],
    mappings: {
      'uci.q06': { A: -1.0, B: 0.2, C: 0.7, D: 1.0 },
      'uci.q10': { A: -1.0, B: 0.2, C: 0.7, D: 1.0 }
    }
  },
  {
    id: 'emotion.logic_affect_integration',
    label: 'Logic-affect integration',
    category: 'emotion_self_regulation',
    definition: 'How emotional information is incorporated alongside factual reasoning during a decision.',
    lowAnchor: 'inspect logic separately',
    highAnchor: 'integrate logic and affect',
    alternatives: ['current emotional intensity', 'decision stakes', 'emotion vocabulary'],
    mappings: {
      'uci.q09': { A: -1.0, B: 0.6, C: 1.0, D: 0.2 }
    }
  },
  {
    id: 'pref.decision.pace',
    label: 'Decision pace',
    category: 'collaboration_preference',
    definition: 'The useful cadence for moving from options to a reversible decision.',
    lowAnchor: 'rapid iteration',
    highAnchor: 'deliberate evaluation',
    alternatives: ['stakes', 'reversibility', 'fatigue'],
    mappings: {
      'uci.q12': { A: -1.0, B: -0.5, C: 0.3, D: 1.0 }
    }
  },
  {
    id: 'cog.explanation.representation',
    label: 'Explanation representation',
    category: 'collaboration_preference',
    definition: 'Which representation makes an accurate explanation usable fastest.',
    lowAnchor: 'concrete verbal sequence',
    highAnchor: 'relational or visual frame',
    alternatives: ['topic', 'audience', 'device', 'accessibility needs'],
    mappings: {
      'uci.q13': { A: -1.0, B: 0.2, C: 1.0, D: 0.5 },
      'uci.q23': { A: 0.1, B: -1.0, C: 1.0, D: 0.3 }
    }
  },
  {
    id: 'social.conflict_orientation',
    label: 'Disagreement navigation',
    category: 'social_interaction',
    definition: 'Which move is most useful first when a disagreement must become a decision.',
    lowAnchor: 'perspective and shared criteria',
    highAnchor: 'synthesis and direct testing',
    alternatives: ['power', 'relationship', 'psychological safety'],
    mappings: {
      'uci.q14': { A: -1.0, B: -0.4, C: 0.6, D: 1.0 }
    }
  },
  {
    id: 'cog.memory.reconstruction_cue',
    label: 'Memory reconstruction cue',
    category: 'cognition',
    definition: 'Which evidence channel is most useful first when reconstructing a past event.',
    lowAnchor: 'external records',
    highAnchor: 'experiential cues',
    alternatives: ['record availability', 'time since event', 'event salience'],
    mappings: {
      'uci.q15': { A: -1.0, B: 0.7, C: 0.1, D: 1.0 }
    }
  },
  {
    id: 'pref.work.flow_environment',
    label: 'Flow support environment',
    category: 'collaboration_preference',
    definition: 'Which structure and time signals are most likely to support sustained engagement.',
    lowAnchor: 'external structure and check-ins',
    highAnchor: 'autonomy and immersion',
    alternatives: ['task type', 'energy', 'attention state', 'deadline'],
    mappings: {
      'uci.q16': { A: -0.8, B: 0.6, C: -1.0, D: 1.0 },
      'uci.q20': { A: -1.0, B: 1.0, C: 0.2, D: 0.0 }
    }
  },
  {
    id: 'social.group_role',
    label: 'Collaboration role signal',
    category: 'social_interaction',
    definition: 'Which information and contribution style becomes useful first in group work.',
    lowAnchor: 'coordination and connection',
    highAnchor: 'analysis and novel direction',
    alternatives: ['team composition', 'formal role', 'status', 'expertise'],
    mappings: {
      'uci.q18': { A: -0.6, B: 0.5, C: -1.0, D: 0.7 },
      'uci.q22': { A: -0.8, B: 0.6, C: -1.0, D: 1.0 }
    }
  },
  {
    id: 'cog.metacognition.strategy_revision',
    label: 'Strategy revision',
    definition: 'Detection of a failing approach and deliberate selection of a different reasoning strategy.',
    lowAnchor: 'persist with the initial strategy',
    highAnchor: 'detect and revise the strategy',
    alternatives: ['self-image', 'feedback safety', 'available alternatives'],
    mappings: {
      'uci.q21': { A: 0.5, B: -0.6, C: 0.1, D: 1.0 },
      'uci.q25': { A: 0.8, B: 0.4, C: 0.3, D: 1.0 }
    }
  },
  {
    id: 'emotion.reset_signal',
    label: 'Reset signal',
    category: 'emotion_self_regulation',
    definition: 'Which signal most reliably indicates that a working reset would help.',
    lowAnchor: 'attention or performance cue',
    highAnchor: 'body or emotional cue',
    alternatives: ['sleep', 'health', 'task demand', 'environment'],
    mappings: {
      'uci.q24': { A: -0.6, B: 0.7, C: -1.0, D: 1.0 }
    }
  }
];

function answerFor(state, questionId) {
  return state.answers[questionId] || { selected: [], ownWords: '' };
}

export function rawEvidence(instrument, state) {
  return instrument.questions.map(question => {
    const answer = answerFor(state, question.id);
    return {
      questionId: question.id,
      question: question.prompt,
      selected: answer.selected.slice(),
      selectedText: question.options.filter(option => answer.selected.includes(option.id)),
      ownWords: answer.ownWords.trim()
    };
  });
}

export function estimateConstruct(instrument, state, spec) {
  const evidence = [];
  const numericSignals = [];
  let ownWordsCount = 0;

  Object.entries(spec.mappings).forEach(([questionId, mapping]) => {
    const answer = answerFor(state, questionId);
    const selectedValues = answer.selected
      .filter(choice => mapping[choice] !== undefined)
      .map(choice => mapping[choice]);
    if (selectedValues.length) {
      numericSignals.push(selectedValues.reduce((a, b) => a + b, 0) / selectedValues.length);
    }
    if (answer.ownWords.trim()) ownWordsCount += 1;
    evidence.push({
      evidenceId: `${spec.id}.${questionId}`,
      sourceType: 'self_report',
      sourceRef: questionId,
      observation: `Selected ${answer.selected.join(', ') || 'none'}.${answer.ownWords.trim() ? ` Own words: ${answer.ownWords.trim()}` : ''}`,
      interpretation: selectedValues.length
        ? `Bounded responses provide a weak directional signal for ${spec.label.toLowerCase()}.`
        : 'Own-words evidence is preserved but not automatically scored.',
      weight: answer.ownWords.trim() ? 0.4 : 0.25
    });
  });

  const score = numericSignals.length
    ? numericSignals.reduce((a, b) => a + b, 0) / numericSignals.length
    : 0;
  let confidence = (numericSignals.length / Object.keys(spec.mappings).length) * 0.4;
  confidence += (ownWordsCount / Object.keys(spec.mappings).length) * 0.08;
  const review = state.reviews[spec.id];
  if (review === 'accurate') confidence = Math.min(0.7, confidence + REVIEW_BONUS);
  if (review === 'correction') confidence = Math.min(confidence, 0.15);

  if (review) {
    evidence.push({
      evidenceId: `${spec.id}.user-review`,
      sourceType: 'correction',
      sourceRef: 'wizard.construct-review',
      observation: review === 'accurate'
        ? 'User marked the hypothesis accurate enough to trial.'
        : 'User marked the hypothesis as needing correction.',
      interpretation: review === 'accurate'
        ? 'Raises product-routing confidence only; it does not validate the construct.'
        : 'Defer adaptation until corrected evidence is captured.',
      weight: 0.25
    });
  }

  return {
    constructId: spec.id,
    category: spec.category || 'cognition',
    operationalDefinition: spec.definition,
    estimate: {
      score: Number(score.toFixed(3)),
      confidence: Number(confidence.toFixed(3)),
      lowAnchor: spec.lowAnchor,
      highAnchor: spec.highAnchor
    },
    signalCount: numericSignals.length,
    evidence,
    alternativeExplanations: spec.alternatives.slice(),
    untested: true
  };
}

export function buildModel(instrument, state, options = {}) {
  const constructs = constructSpecs
    .map(spec => estimateConstruct(instrument, state, spec))
    .filter(construct => construct.signalCount > 0);
  return {
    modelVersion: instrument.modelVersion || '0.2',
    status: 'hypothesis',
    subjectId: options.subjectId || 'local-preview',
    generatedAt: options.generatedAt || new Date().toISOString(),
    constructs,
    limitations: [
      'Reconstructed scenario wording and a small self-report evidence set.',
      'Every structured question contributes to at least one candidate construct.',
      'Own-words responses are preserved verbatim but are not automatically interpreted in v0.1.',
      'User confirmation changes trial eligibility, not scientific validity.'
    ],
    provenance: {
      instrument: instrument.instrumentId,
      instrumentVersion: instrument.instrumentVersion,
      sourceRevision: instrument.provenance.sourceRevision,
      notes: 'Reconstructed 25-item instrument; full structured-response coverage across candidate constructs.'
    }
  };
}

function makeRule(state, ruleId, construct, mode, behavior, guardrail) {
  const review = state.reviews[construct.constructId];
  const userDisabled = state.ruleOverrides?.[ruleId] === 'disabled';
  const applied = !userDisabled && construct.estimate.confidence >= ACTIVATION_THRESHOLD && review === 'accurate';
  return {
    ruleId,
    constructId: construct.constructId,
    mode,
    status: userDisabled ? 'disabled' : applied ? 'applied' : 'deferred',
    behavior,
    guardrail,
    reason: userDisabled
      ? 'user_disabled'
      : applied
      ? 'user_reviewed_hypothesis_above_trial_threshold'
      : review === 'correction' ? 'user_correction_required' : 'low_confidence_or_not_reviewed',
    basis: { score: construct.estimate.score, confidence: construct.estimate.confidence }
  };
}

export function buildAdapterPlan(model, state) {
  const globalOrder = model.constructs.find(item => item.constructId === 'cog.representation.global_local_order');
  const revision = model.constructs.find(item => item.constructId === 'cog.metacognition.strategy_revision');
  const globalFirst = globalOrder?.estimate.score >= 0.2;
  const componentFirst = globalOrder?.estimate.score <= -0.2;
  const start = globalFirst
    ? 'Present the end-state or system map before the component sequence.'
    : componentFirst
      ? 'Present the component sequence before the system-level synthesis.'
      : 'Present a compact system frame and component sequence together.';
  const complement = globalFirst
    ? 'Follow the system frame with a concise dependency checklist.'
    : componentFirst
      ? 'Follow the component sequence with a system map showing relationships.'
      : 'Offer the alternate representation when the first explanation does not land.';

  const rules = [];
  if (globalOrder) {
    rules.push(
      makeRule(state, 'mirror.representation-order', globalOrder, 'mirror', start, 'Do not omit dependencies or material uncertainty.'),
      makeRule(state, 'complement.alternate-representation', globalOrder, 'complement', complement, 'Avoid duplicating the whole answer.'),
      makeRule(state, 'counterbalance.dependency-audit', globalOrder, 'counterbalance', 'Before a high-stakes commitment, check what the preferred representation may hide.', 'Trigger only when stakes or task evidence justify it.')
    );
  }
  if (revision) {
    rules.push(
      makeRule(state, 'counterbalance.strategy-revision', revision, 'counterbalance', 'After repeated failure, name the current assumption and test one alternate strategy.', 'Do not challenge reflexively; require failure evidence.')
    );
  }

  return {
    adapterVersion: '0.1',
    status: 'hypothesis',
    activationThreshold: ACTIVATION_THRESHOLD,
    rules,
    untouchedLayers: ['approvalDiet', 'protocols']
  };
}

export function buildEvidencePackage(instrument, state, options = {}) {
  const cognitiveModel = buildModel(instrument, state, options);
  return {
    exportVersion: '0.2',
    exportedAt: options.generatedAt || new Date().toISOString(),
    rawEvidence: rawEvidence(instrument, state),
    cognitiveModel,
    adapterPlan: buildAdapterPlan(cognitiveModel, state),
    collaborationPolicy: {
      approvalDiet: {
        version: '0.1',
        status: 'separate_unconfigured',
        inferredFromCognition: false,
        rules: []
      },
      protocols: {
        version: '0.1',
        status: 'separate_unconfigured',
        inferredFromCognition: false,
        active: []
      }
    }
  };
}
